import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback, useState } from 'react';
import { onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  fetchShowFolders,
  fetchShowResources,
  resolveDownloadUrl,
  handleDeleteCategory,
  handleUpdateFile,
  handleMoveFile,
  handleDeleteFile,
  ShowFolderRecord,
  FoldersByCategory,
  FolderCounts,
} from '../components/ResourcesTab/ResourcesDataHandlers';

// Type definitions for mutation data
interface AddCustomFolderData {
  name: string;
  icon: string;
  supportsFolders?: boolean;
  description?: string;
  parentFolderId?: string;
}

interface UpdateFileData {
  resourceId: string;
  name: string;
}

interface MoveFileData {
  resourceId: string;
  /** Destination folder path, or '' for the root of Resources. */
  folderPath: string;
}

// Query keys for consistent caching. `byShow` is the shared prefix of both
// leaves, so an invalidation aimed at it still reaches folders and files.
export const resourcesQueryKeys = {
  all: ['resources'] as const,
  byShow: (showId: string) => [...resourcesQueryKeys.all, showId] as const,
  folders: (showId: string) => [...resourcesQueryKeys.byShow(showId), 'folders'] as const,
  files: (showId: string) => [...resourcesQueryKeys.byShow(showId), 'files'] as const,
};

export type SubcategoryFiles = { [folderPath: string]: any[] };

// Stable identities for the not-yet-loaded case. Returning a fresh `[]` or
// `{}` on every render would give consumers a new dependency identity each
// time, re-running their effects for no reason.
const EMPTY_FOLDERS: ShowFolderRecord[] = [];
const EMPTY_FOLDERS_BY_CATEGORY: FoldersByCategory = { singing: [], dancing: [], acting: [] };
const EMPTY_FOLDER_COUNTS: FolderCounts = { singing: 0, dancing: 0, acting: 0, all: 0 };
const EMPTY_FILES: SubcategoryFiles = {};

const QUERY_TUNING = {
  staleTime: 5 * 60 * 1000, // 5 minutes - same as other tabs
  gcTime: 30 * 60 * 1000, // 30 minutes
  retry: 2,
  refetchOnWindowFocus: false,
} as const;

// Folders and files are fetched as two independent queries. getShowFolders
// returns a few small documents; getResources reads every resource in the
// show. Keeping them separate lets the folder grid render on the fast
// response rather than waiting for the slow one.
export const foldersQueryOptions = (showId: string) => ({
  queryKey: resourcesQueryKeys.folders(showId),
  queryFn: () => fetchShowFolders(showId),
  ...QUERY_TUNING,
});

export const filesQueryOptions = (showId: string) => ({
  queryKey: resourcesQueryKeys.files(showId),
  queryFn: () => fetchShowResources(showId),
  ...QUERY_TUNING,
});

// Warm both queries without mounting the tab. Used by the show page to
// prefetch on tab hover and again when the browser goes idle.
export const prefetchResourcesData = (queryClient: QueryClient, showId: string) => {
  if (!showId) return;
  queryClient.prefetchQuery(foldersQueryOptions(showId));
  queryClient.prefetchQuery(filesQueryOptions(showId));
};

// Run a callback when the browser is next idle, falling back to a timeout on
// Safari, which still has no requestIdleCallback.
const onIdle = (callback: () => void, timeoutMs = 2000): (() => void) => {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (ric) {
    const handle = ric(callback, { timeout: timeoutMs });
    return () => (window as any).cancelIdleCallback?.(handle);
  }
  const handle = setTimeout(callback, timeoutMs);
  return () => clearTimeout(handle);
};

// Download URLs resolved for one show's legacy rows (see the backfill in
// useResourcesData). `resolved` outlives any one version of the cache;
// `inFlight` stops a lookup starting twice; `live` goes false when the show
// changes or the hook unmounts, so a late result is dropped.
interface UrlBackfillLedger {
  showId: string;
  resolved: Map<string, string>;
  inFlight: Set<string>;
  failed: Set<string>;
  live: boolean;
}

// Patch resolved URLs into rows that still lack one. Hands the cache back
// untouched when there is nothing to patch (or no cache at all), so an
// unchanged cache keeps its identity and does not re-render anything.
const applyResolvedUrls = (
  queryClient: QueryClient,
  key: readonly unknown[],
  urls: Map<string, string>,
) => {
  queryClient.setQueryData<SubcategoryFiles>(key, old => {
    if (!old) return old;
    const needsUrl = (f: any) => !f?.downloadUrl && urls.has(f?.resourceId);
    if (!Object.values(old).some(files => files.some(needsUrl))) return old;
    const next: SubcategoryFiles = {};
    for (const [folderPath, files] of Object.entries(old)) {
      next[folderPath] = files.map((f: any) =>
        needsUrl(f) ? { ...f, downloadUrl: urls.get(f.resourceId) } : f
      );
    }
    return next;
  });
};

// Main hook for resources data with real-time updates
export const useResourcesData = (showId: string) => {
  const queryClient = useQueryClient();

  // Track last known status for each resource to prevent unnecessary invalidations
  const lastKnownStatusRef = useRef<Map<string, { compressionStatus?: string | null, thumbnailStatus?: string | null }>>(new Map());
  
  // Proper debounce timer refs
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addedDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Properly debounced invalidation. Only the files query is refetched -
  // compression and thumbnail status live on resource documents, so the
  // folder query has nothing to learn from these changes.
  const triggerInvalidate = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: resourcesQueryKeys.files(showId),
        refetchType: 'active',
      });
    }, 250);
  }, [queryClient, showId]);

  // Longer debounce for newly added resources: bulk uploads create documents
  // in bursts spread over minutes, and getResources is rate-limited, so
  // coalesce aggressively rather than refetching per document.
  const triggerAddedInvalidate = useCallback(() => {
    if (addedDebounceTimerRef.current) clearTimeout(addedDebounceTimerRef.current);
    addedDebounceTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: resourcesQueryKeys.files(showId),
        refetchType: 'active',
      });
    }, 3000);
  }, [queryClient, showId]);
  
  const foldersQuery = useQuery({ ...foldersQueryOptions(showId), enabled: !!showId });
  const filesQuery = useQuery({ ...filesQueryOptions(showId), enabled: !!showId });

  // The listener below streams every resource document for the show. That is
  // the same data the files query is fetching, so opening the channel during
  // the initial load makes the two compete for bandwidth on the critical
  // path. Wait until the files query has settled, then subscribe on the next
  // idle frame.
  //
  // The gate records which show it opened for rather than a bare boolean. A
  // boolean reset by an effect was still true in the render where showId
  // changed, so the new show was subscribed before its own files had
  // settled, torn down a render later and subscribed again - a throwaway
  // whole-collection listener on the new show's critical path.
  const [listenerReadyFor, setListenerReadyFor] = useState<string | null>(null);
  const listenerReady = !!showId && listenerReadyFor === showId;
  const filesSettled = filesQuery.isFetched;

  useEffect(() => {
    if (!showId || !filesSettled || listenerReady) return;
    return onIdle(() => setListenerReadyFor(showId));
  }, [showId, filesSettled, listenerReady]);

  // Set up real-time listener for resource document updates
  useEffect(() => {
    if (!showId || !listenerReady) return;

    // Listen to all resources for this show to catch thumbnail, compression, and size updates
    const q = query(
      collection(db, 'resources'),
      where('showId', '==', showId)
    );
    
    // The initial snapshot reports every existing document as 'added', so
    // only treat additions as new resources after that first delivery.
    let isInitialSnapshot = true;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let hasRelevantChanges = false;
        let hasNewRemoteResources = false;

        snapshot.docChanges().forEach((change) => {
          const docId = change.doc.id;
          const docData = change.doc.data();
          const currentCompression = docData.compressionStatus ?? null;
          const currentThumbnail = docData.thumbnailStatus ?? null;

          if (change.type === 'added') {
            // Seed the map so first metadata update doesn't look like a change
            lastKnownStatusRef.current.set(docId, {
              compressionStatus: currentCompression,
              thumbnailStatus: currentThumbnail,
            });

            if (!isInitialSnapshot) {
              // A resource was created after we mounted. Our own uploads are
              // inserted into the cache directly by UploadQueueContext, so
              // only refetch when the document is not in the cache yet (e.g.
              // a colleague uploaded from another device).
              const cached = queryClient.getQueryData<SubcategoryFiles>(
                resourcesQueryKeys.files(showId)
              );
              const inCache = cached
                ? (Object.values(cached) as any[][])
                    .flat()
                    .some((f: any) => f.resourceId === docId)
                : false;
              if (!inCache) {
                hasNewRemoteResources = true;
              }
            }
            return;
          }

          if (change.type === 'removed') {
            // Prune deleted resources to prevent map from growing forever
            lastKnownStatusRef.current.delete(docId);
            return;
          }

          if (change.type === 'modified') {
            const prev = lastKnownStatusRef.current.get(docId);
            const compressionChanged = prev?.compressionStatus !== currentCompression;
            const thumbnailChanged = prev?.thumbnailStatus !== currentThumbnail;

            if (compressionChanged || thumbnailChanged) {
              hasRelevantChanges = true;
              lastKnownStatusRef.current.set(docId, {
                compressionStatus: currentCompression,
                thumbnailStatus: currentThumbnail,
              });
            }
          }
        });

        isInitialSnapshot = false;

        if (hasRelevantChanges) triggerInvalidate();
        if (hasNewRemoteResources) triggerAddedInvalidate();
      },
      (error) => {
        console.error('🔄 Error in resources real-time listener:', error);
      }
    );

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (addedDebounceTimerRef.current) {
        clearTimeout(addedDebounceTimerRef.current);
        addedDebounceTimerRef.current = null;
      }
      unsubscribe();
    };
  }, [showId, listenerReady, triggerInvalidate, triggerAddedInvalidate, queryClient]);

  // Background download-URL backfill. Resources written before addResource
  // persisted downloadUrl have only a storagePath, and cards pick their layout
  // (and their thumbnail source) from downloadUrl. Resolving those used to
  // happen inside the fetch, holding first paint behind one Storage round trip
  // per file; now the files render immediately and the URLs are patched into
  // the cache when they arrive. Shows with no legacy rows do no work at all.
  //
  // The ledger is the source of truth rather than the cache: every refetch
  // replaces the cache with the server's rows, which still carry no
  // downloadUrl, so resolved URLs are kept here and re-applied to whatever
  // the cache holds. One ledger per show per mount; the show-scoped effect
  // below swaps it, and it is declared first so the new ledger is in place
  // before the backfill effect runs in the same commit.
  const backfillRef = useRef<UrlBackfillLedger | null>(null);
  const filesData = filesQuery.data;

  useEffect(() => {
    const ledger: UrlBackfillLedger = {
      showId,
      resolved: new Map(),
      inFlight: new Set(),
      failed: new Set(),
      live: true,
    };
    backfillRef.current = ledger;
    // A show change or unmount is the only real cancellation. Lookups still
    // running are abandoned and their ids released, so the next ledger for
    // this show looks them up again.
    return () => {
      ledger.live = false;
      ledger.inFlight.clear();
    };
  }, [showId]);

  useEffect(() => {
    const ledger = backfillRef.current;
    if (!showId || !filesData || !ledger || ledger.showId !== showId) return;

    const key = resourcesQueryKeys.files(showId);
    const missing = (Object.values(filesData) as any[][])
      .flat()
      .filter((f: any) => f?.storagePath && !f.downloadUrl);
    if (missing.length === 0) return;

    // A refetch brought back rows whose URL is already known - put it back
    // without another Storage round trip.
    if (missing.some((f: any) => ledger.resolved.has(f.resourceId))) {
      applyResolvedUrls(queryClient, key, ledger.resolved);
    }

    // Only look up what is neither known nor already being looked up. A
    // failed lookup is not retried for the life of the ledger: the row is
    // left alone, and retrying on every cache change (each upload, each
    // status refetch) would re-query Storage for an object that is not there.
    const pending = missing.filter((f: any) =>
      !ledger.resolved.has(f.resourceId) &&
      !ledger.inFlight.has(f.resourceId) &&
      !ledger.failed.has(f.resourceId)
    );
    if (pending.length === 0) return;
    pending.forEach((f: any) => ledger.inFlight.add(f.resourceId));

    // Deliberately no cleanup: the cache changing mid-lookup (an upload
    // landing, a refetch) re-runs this effect, and that must not throw the
    // results away. They are recorded and applied to the cache as it is now.
    (async () => {
      const results = await Promise.all(
        pending.map(async (f: any) => ({
          resourceId: f.resourceId as string,
          downloadUrl: await resolveDownloadUrl(f.storagePath),
        }))
      );
      if (!ledger.live) return;

      let anyResolved = false;
      for (const { resourceId, downloadUrl } of results) {
        ledger.inFlight.delete(resourceId);
        if (downloadUrl) {
          ledger.resolved.set(resourceId, downloadUrl);
          anyResolved = true;
        } else {
          ledger.failed.add(resourceId);
        }
      }
      if (anyResolved) applyResolvedUrls(queryClient, key, ledger.resolved);
    })();
  }, [showId, filesData, queryClient]);

  return {
    folders: foldersQuery.data?.folders ?? EMPTY_FOLDERS,
    foldersByCategory: foldersQuery.data?.foldersByCategory ?? EMPTY_FOLDERS_BY_CATEGORY,
    folderCounts: foldersQuery.data?.folderCounts ?? EMPTY_FOLDER_COUNTS,
    subcategoryFiles: filesData ?? EMPTY_FILES,
    // Granular flags so the folder grid does not sit behind the file fetch.
    isLoadingFolders: foldersQuery.isLoading,
    isLoadingFiles: filesQuery.isLoading,
    foldersLoaded: !!foldersQuery.data,
    filesLoaded: !!filesData,
    error: (foldersQuery.error ?? filesQuery.error) as Error | null,
  };
};

// Hook for mutations (folder and file operations)
export const useResourcesMutations = (showId: string) => {
  const queryClient = useQueryClient();
  
  // Invalidate and refetch resources data after mutations
  const invalidateResourcesData = async () => {
    console.log('🔄 React Query: Invalidating resources data for show:', showId);
    await queryClient.invalidateQueries({ queryKey: resourcesQueryKeys.byShow(showId) });
  };

  // Delete folder mutation
  const deleteFolder = useMutation({
    mutationFn: async (folderId: string) => {
      console.log('🗑️ Deleting folder:', folderId);
      await handleDeleteCategory(showId, folderId);
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
    onError: (error) => {
      console.error('Error deleting folder:', error);
    },
  });

  // Update file mutation
  const updateFile = useMutation({
    mutationFn: async (data: UpdateFileData) => {
      console.log('📝 Updating file:', data.resourceId);
      await handleUpdateFile(data.resourceId, data.name);
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
    onError: (error) => {
      console.error('Error updating file:', error);
    },
  });

  // Move file mutation
  const moveFile = useMutation({
    mutationFn: async (data: MoveFileData) => {
      console.log('📁 Moving file:', data.resourceId, '->', data.folderPath || '<root>');
      await handleMoveFile(data.resourceId, data.folderPath);
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
    onError: (error) => {
      console.error('Error moving file:', error);
    },
  });

  // Delete file mutation
  const deleteFile = useMutation({
    mutationFn: async (resourceId: string) => {
      console.log('🗑️ Deleting file:', resourceId);
      await handleDeleteFile(resourceId);
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
    onError: (error) => {
      console.error('Error deleting file:', error);
    },
  });

  // Add custom folder (folders hold mixed media - no mediaType sent)
  const addCustomFolder = useMutation({
    mutationFn: async (data: AddCustomFolderData) => {
      const addCustomFolderFn = httpsCallable(functions, 'addCustomFolder');
      const result = await addCustomFolderFn({
        showId,
        name: data.name.trim(),
        icon: data.icon,
        supportsFolders: data.supportsFolders ?? false,
        description: data.description,
        ...(data.parentFolderId ? { parentFolderId: data.parentFolderId } : {}),
      });
      return result.data;
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
  });

  // Reorder folders
  const reorderFolders = useMutation({
    mutationFn: async (folderIds: string[]) => {
      const reorderFoldersFn = httpsCallable(functions, 'reorderFolders');
      await reorderFoldersFn({ showId, folderIds });
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
  });

  // Update folder with new fields (mediaType, supportsFolders, sortOrder)
  const updateFolderSettings = useMutation({
    mutationFn: async (data: { folderId: string; name?: string; icon?: string; mediaType?: string; supportsFolders?: boolean; sortOrder?: number }) => {
      const updateShowFolderFn = httpsCallable(functions, 'updateShowFolder');
      const result = await updateShowFolderFn({ showId, ...data });
      // On a rename the backend reports the paths it migrated between, so the
      // caller can re-point uploads that are still in flight.
      return result.data as { success: boolean; migratedResources: number; oldPath: string | null; newPath: string | null };
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
  });

  // Move a folder, and everything in it, under another folder - or to the top
  // level with a null parent. Like a rename, the backend reports the paths it
  // migrated between so uploads still in flight can follow.
  const moveFolder = useMutation({
    mutationFn: async (data: { folderId: string; parentFolderId: string | null }) => {
      const moveShowFolderFn = httpsCallable(functions, 'moveShowFolder');
      const result = await moveShowFolderFn({ showId, ...data });
      return result.data as { success: boolean; moved: boolean; migratedResources: number; oldPath: string | null; newPath: string | null };
    },
    onSuccess: async () => {
      await invalidateResourcesData();
    },
  });

  return {
    deleteFolder,
    updateFile,
    moveFile,
    moveFolder,
    deleteFile,
    addCustomFolder,
    reorderFolders,
    updateFolderSettings,
    invalidateResourcesData,
  };
};

// Export types for other components
export type { UpdateFileData };